import React, { useMemo, useState } from "react";
import { CheckSquare, Edit3, Users, Home, AlertTriangle, MapPin, Award } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import ColumnVisibilityMenu from "../../../../components/common/ColumnVisibilityMenu";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import PrimaryCell from "../../../../components/common/PrimaryCell";
import NavigateLink from "../../../../components/common/NavigateLink";
import { MAROON } from "../../../../components/common/brandColors";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import CustomerPicker from "../../components/CustomerPicker";
import EmployeeAvatar from "../../../reports/oneStop/components/EmployeeAvatar";
import { useEmployeePhotoCache } from "../../../masters/employees/useEmployeePhotoCache";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { useListClientVisitsQuery } from "../../salesApi";
import { useCanViewAllClientVisits } from "../../useSalesPermissions";
import ClientVisitDetailModal from "../components/ClientVisitDetailModal";
import { ClientVisitListPrintButton, ClientVisitPrintButton } from "../components/ClientVisitPrint";

/*
  Client Visits report (Batch182) - the reporting end of the printed
  "CLIENT VISIT REPORT" pad.

  Read-only by design: visits are recorded in the field from the Flutter app,
  where the QR scanner and the GPS gate live. There is no create/edit here,
  and no "delete" - a visit is a dated statement about what somebody saw on
  site, so correcting one means recording another.

  Filters are deliberately a mix:
   - date range, branch, customer and checklist-or-not go to the server (the
     proc indexes on branch+date and customer+date);
   - visitor and the free-text search run client-side over the loaded page,
     because the visitor list is short and only knowable from the rows
     themselves - asking the API for a list of people would need
     Sales.BookForOthers, a permission nobody needs just to read a report.
*/

// The proc caps @Take at 2000. One page that size covers any realistic date
// range for this report, and keeps the client-side visitor filter honest
// (it only ever filters what is actually loaded).
const PAGE_SIZE = 2000;

const CHECKLIST_OPTIONS = [
  { value: "", label: "All visits" },
  { value: "true", label: "With checklist" },
  { value: "false", label: "Remarks only" },
];

const RATING_BADGE = {
  GOOD: "badge-linesuccess",
  AVERAGE: "badge-linewarning",
  POOR: "badge-linedanger",
};

const ratingLabel = (value) =>
  !value ? "" : { GOOD: "Good", AVERAGE: "Average", POOR: "Poor" }[value] || value;

const coverageLabel = (value) =>
  !value ? "" : { STANDARD: "Standard", UNDER_DEPLOYMENT: "Under Deployment" }[value] || value;

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const RatingBadge = ({ value }) =>
  !value ? <span className="text-muted">—</span> : (
    <span className={`badge ${RATING_BADGE[value] || "badge-linesecondary"}`}>{ratingLabel(value)}</span>
  );

// One accented KPI tile: icon chip, muted label, the figure loud and coloured.
// `dim` greys a zero that is GOOD news (no Poor ratings) so it doesn't read as
// an alarm.
const StatTile = ({ icon: Icon, label, value, color, note, dim = false }) => {
  const tone = dim ? "#98A2B3" : color;
  return (
    <div className="col-6 col-md">
      <div
        className="h-100"
        style={{
          background: "#fff",
          border: `1px solid ${tone}2E`,
          borderLeft: `4px solid ${tone}`,
          borderRadius: 10,
          padding: "8px 12px",
          boxShadow: `0 2px 8px ${tone}14`,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{ width: 26, height: 26, background: `${tone}1F` }}
          >
            <Icon size={14} color={tone} />
          </span>
          <span style={{ fontSize: 11.5, color: "#667085", fontWeight: 600 }}>{label}</span>
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: tone, lineHeight: 1.25 }}>{value}</div>
        {note ? <div style={{ fontSize: 10.5, color: "#98A2B3" }}>{note}</div> : null}
      </div>
    </div>
  );
};

// Every column the table can show. Hidden-by-default ones are the checklist
// detail: most people open this report to see who visited whom and what they
// said, and only widen it when auditing the guarding/alarm findings.
const ALL_COLUMNS = [
  { key: "visitedAt", title: "Date" },
  { key: "customer", title: "Customer" },
  { key: "branchCode", title: "Branch" },
  { key: "visitedByName", title: "Visited By" },
  { key: "contactPerson", title: "Contact" },
  { key: "location", title: "Location" },
  { key: "remarks", title: "Remarks" },
  { key: "checklist", title: "Checklist" },
  { key: "guards", title: "Guards" },
  { key: "guardInCharge", title: "Guard In Charge" },
  { key: "uniformRating", title: "Uniform" },
  { key: "appearanceRating", title: "Appearance" },
  { key: "presentationRating", title: "Presentation" },
  { key: "guardingCoverage", title: "Guard Coverage" },
  { key: "alarmDevices", title: "Alarm Equipment" },
  { key: "installationQualityRating", title: "Install Quality" },
  { key: "alarmCoverageRating", title: "Alarm Coverage" },
  { key: "clientRemarks", title: "Client Remarks" },
  { key: "visitNumber", title: "Visit No." },
  { key: "print", title: "Print" },
];

const DEFAULT_HIDDEN = [
  "guardingCoverage",
  "installationQualityRating",
  "alarmCoverageRating",
  "clientRemarks",
  "visitNumber",
];

const alarmDevicesOf = (row) =>
  [
    row.hasManualAlarm ? "Manual Alarm" : null,
    row.hasAutoAlarm ? "A/Alarm" : null,
    row.hasElectricFence ? "Electric Fence" : null,
    row.hasFireAlarm ? "Fire Alarm" : null,
    row.hasCctv ? "C.C.T.V" : null,
  ].filter(Boolean);

const totalGuardsOf = (row) => {
  if (!row.hasOperationsChecklist) return null;
  const parts = [row.guardsDay, row.guardsNight, row.dogAndHandler].filter((n) => n != null);
  return parts.length === 0 ? null : parts.reduce((a, b) => a + b, 0);
};

const ClientVisitsReportPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("LAST_30_DAYS");
  const canViewAll = useCanViewAllClientVisits();

  const [checklistFilter, setChecklistFilter] = useState("");
  const [visitorFilter, setVisitorFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(DEFAULT_HIDDEN);
  const [openVisit, setOpenVisit] = useState(null);

  const toggleColumn = (key) =>
    setHiddenColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  const queryParams = useMemo(
    () => ({
      branchId: selectedBranchId ?? undefined,
      custId: selectedCustomerOption?.customer?.custId ?? undefined,
      dateFrom: dateFrom || undefined,
      // The proc filters VisitedAt < @DateTo, so send the day AFTER the
      // picked end date - otherwise a visit recorded at 3pm on the last day
      // silently drops out of its own range.
      dateTo: dateTo ? new Date(new Date(dateTo).getTime() + 86400000).toISOString().slice(0, 10) : undefined,
      hasChecklist: checklistFilter === "" ? undefined : checklistFilter === "true",
      page: 1,
      pageSize: PAGE_SIZE,
    }),
    [selectedBranchId, selectedCustomerOption, dateFrom, dateTo, checklistFilter],
  );

  const { data, isLoading, isError, error } = useListClientVisitsQuery(queryParams);
  // Memoized so the `?? []` fallback isn't a fresh array on every render -
  // otherwise the useMemos below it re-run each time and the visitor list
  // rebuilds continuously.
  const allRows = useMemo(() => data?.items ?? [], [data]);

  // Every employee this page names, fetched once each (the table rows and the
  // open modal are all drawn from `allRows`, so one cache covers both).
  // Batch182b added `visitedByEmployeeRecordGuid`; it is null for a service
  // account or a user with no employee link, and the avatar falls back to
  // initials for those - a photo is never looked up by name.
  const photoUrls = useEmployeePhotoCache(allRows, (row) => row?.visitedByEmployeeRecordGuid);

  const visitorNames = useMemo(
    () => [...new Set(allRows.map((r) => r.visitedByName).filter(Boolean))].sort(),
    [allRows],
  );

  const rows = useMemo(() => {
    const needle = searchInput.trim().toLowerCase();
    return allRows.filter((r) => {
      if (visitorFilter && r.visitedByName !== visitorFilter) return false;
      if (!needle) return true;
      return [r.custName, r.visitNumber, r.contactPerson, r.remarks, r.clientRemarks, r.visitedByName]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
    });
  }, [allRows, visitorFilter, searchInput]);

  // One description of what is on screen, shared by the Export toolbar's
  // subtitle and the list print's header - so an exported PDF and a printed
  // sheet can never claim different filters for the same view.
  const printFilterLines = [
    `Branch: ${branchCode || "All"}`,
    `Date: ${formatDate(dateFrom)} to ${formatDate(dateTo)}`,
    visitorFilter ? `Visited by: ${visitorFilter}` : null,
    selectedCustomerOption ? `Customer: ${selectedCustomerOption.customer?.custName}` : null,
    checklistFilter === "true" ? "With checklist only" : checklistFilter === "false" ? "Remarks only" : null,
  ].filter(Boolean);

  const stats = useMemo(() => {
    const withChecklist = rows.filter((r) => r.hasOperationsChecklist).length;
    const customers = new Set(rows.map((r) => `${r.branchId}-${r.custId}`)).size;
    const visitors = new Set(rows.map((r) => r.visitedByUserId)).size;
    const poor = rows.filter((r) =>
      [r.uniformRating, r.appearanceRating, r.presentationRating, r.installationQualityRating, r.alarmCoverageRating]
        .includes("POOR"),
    ).length;
    return { total: rows.length, withChecklist, customers, visitors, poor };
  }, [rows]);

  const columnDefs = {
    visitedAt: {
      title: "Date",
      dataIndex: "visitedAt",
      render: (value) => <span className="text-nowrap">{formatDateTime(value)}</span>,
    },
    customer: {
      title: "Customer",
      dataIndex: "custName",
      // Leads with the customer, with the CV- number underneath - the standing
      // list rule (feedback_lists_lead_with_party_not_number). No `to`: a visit
      // has no detail route, it opens in a modal from the row click.
      render: (value, record) => (
        <PrimaryCell
          primary={value}
          secondary={record.visitNumber}
          extra={record.siteLabel || undefined}
          leading={
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{
                width: 28,
                height: 28,
                background: record.hasOperationsChecklist ? `${MAROON}1F` : "#F2F4F7",
              }}
              title={record.hasOperationsChecklist ? "Operations checklist filled" : "Remarks only"}
            >
              {record.hasOperationsChecklist
                ? <CheckSquare size={14} color={MAROON} />
                : <Edit3 size={14} color="#98A2B3" />}
            </span>
          }
        />
      ),
    },
    branchCode: { title: "Branch", dataIndex: "branchCode" },
    visitedByName: {
      title: "Visited By",
      dataIndex: "visitedByName",
      // An employee's name always carries their enrolment photo in this app.
      render: (value, record) => (
        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          <EmployeeAvatar name={value} photoUrl={photoUrls[record.visitedByEmployeeRecordGuid]} />
          <span className="text-truncate">{value}</span>
        </div>
      ),
    },
    contactPerson: {
      title: "Contact",
      dataIndex: "contactPerson",
      render: (value, record) => (
        <div>
          <div>{value || <span className="text-muted">—</span>}</div>
          {record.contactDesignation ? <div className="text-muted small">{record.contactDesignation}</div> : null}
        </div>
      ),
    },
    location: {
      title: "Location",
      dataIndex: "latitude",
      // Directions to where the visit was recorded (the GPS fix taken with it).
      render: (value, record) =>
        record.latitude != null && record.longitude != null
          ? <NavigateLink latitude={record.latitude} longitude={record.longitude} />
          : <span className="text-muted">—</span>,
    },
    remarks: {
      title: "Remarks",
      dataIndex: "remarks",
      render: (value) => (
        <span title={value} style={{ display: "inline-block", maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value}
        </span>
      ),
    },
    checklist: {
      title: "Checklist",
      dataIndex: "hasOperationsChecklist",
      render: (value) =>
        value ? <span className="badge badge-linesuccess">Yes</span> : <span className="text-muted">—</span>,
    },
    guards: {
      title: "Guards",
      dataIndex: "guardsDay",
      render: (value, record) => {
        const total = totalGuardsOf(record);
        if (total == null) return <span className="text-muted">—</span>;
        return (
          <span className="text-nowrap" title={`Day ${record.guardsDay ?? 0} · Night ${record.guardsNight ?? 0} · Dog & handler ${record.dogAndHandler ?? 0}`}>
            {total}
          </span>
        );
      },
    },
    guardInCharge: {
      title: "Guard In Charge",
      dataIndex: "guardInChargeName",
      // A person now (Batch182c), not a count - so a named badge with the
      // employee code, which is how this business identifies a guard.
      render: (value, record) =>
        value ? (
          <span
            className="d-inline-flex align-items-center gap-1"
            style={{
              fontSize: 11.5, fontWeight: 700, color: "#C0392B",
              background: "#C0392B14", border: "1px solid #C0392B3D",
              borderRadius: 999, padding: "2px 8px",
            }}
            title={record.guardInChargeEmpCode ? `Employee ${record.guardInChargeEmpCode}` : undefined}
          >
            <Award size={11} />
            {record.guardInChargeEmpCode ? `${record.guardInChargeEmpCode} — ${value}` : value}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    uniformRating: { title: "Uniform", dataIndex: "uniformRating", render: (v) => <RatingBadge value={v} /> },
    appearanceRating: { title: "Appearance", dataIndex: "appearanceRating", render: (v) => <RatingBadge value={v} /> },
    presentationRating: { title: "Presentation", dataIndex: "presentationRating", render: (v) => <RatingBadge value={v} /> },
    guardingCoverage: {
      title: "Guard Coverage",
      dataIndex: "guardingCoverage",
      render: (v) => (v ? coverageLabel(v) : <span className="text-muted">—</span>),
    },
    alarmDevices: {
      title: "Alarm Equipment",
      dataIndex: "hasCctv",
      render: (value, record) => {
        const devices = alarmDevicesOf(record);
        if (devices.length === 0) return <span className="text-muted">—</span>;
        return (
          <div className="d-flex flex-wrap gap-1">
            {devices.map((d) => (
              <span key={d} className="badge badge-linesecondary">{d}</span>
            ))}
          </div>
        );
      },
    },
    installationQualityRating: {
      title: "Install Quality",
      dataIndex: "installationQualityRating",
      render: (v) => <RatingBadge value={v} />,
    },
    alarmCoverageRating: {
      title: "Alarm Coverage",
      dataIndex: "alarmCoverageRating",
      render: (v) => <RatingBadge value={v} />,
    },
    clientRemarks: {
      title: "Client Remarks",
      dataIndex: "clientRemarks",
      render: (value) =>
        value ? (
          <span title={value} style={{ display: "inline-block", maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    visitNumber: { title: "Visit No.", dataIndex: "visitNumber" },
    print: {
      title: "",
      dataIndex: "recordGuid",
      // Print one visit's sheet straight from its row, without opening it -
      // the row click still opens the modal (the button stops propagation).
      render: (value, record) => <ClientVisitPrintButton visit={record} compact />,
    },
  };

  const columns = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.key)).map((c) => columnDefs[c.key]);

  const exportColumns = [
    { title: "Date", accessor: (r) => formatDateTime(r.visitedAt) },
    { title: "Visit No.", accessor: (r) => r.visitNumber },
    { title: "Customer", accessor: (r) => (r.siteLabel ? `${r.custName} - ${r.siteLabel}` : r.custName) },
    { title: "Branch", accessor: (r) => r.branchCode },
    { title: "Visited By", accessor: (r) => r.visitedByName },
    { title: "Contact", accessor: (r) => r.contactPerson || "" },
    { title: "Designation", accessor: (r) => r.contactDesignation || "" },
    { title: "Mobile", accessor: (r) => r.contactMobile || "" },
    { title: "Remarks", accessor: (r) => r.remarks || "" },
    { title: "Client Remarks", accessor: (r) => r.clientRemarks || "" },
    { title: "Checklist", accessor: (r) => (r.hasOperationsChecklist ? "Yes" : "No") },
    { title: "Guards Day", accessor: (r) => r.guardsDay ?? "" },
    { title: "Guards Night", accessor: (r) => r.guardsNight ?? "" },
    { title: "Dog & Handler", accessor: (r) => r.dogAndHandler ?? "" },
    { title: "Guard In Charge Code", accessor: (r) => r.guardInChargeEmpCode ?? "" },
    { title: "Guard In Charge", accessor: (r) => r.guardInChargeName || "" },
    { title: "Uniform", accessor: (r) => ratingLabel(r.uniformRating) },
    { title: "Appearance", accessor: (r) => ratingLabel(r.appearanceRating) },
    { title: "Presentation", accessor: (r) => ratingLabel(r.presentationRating) },
    { title: "Guard Coverage", accessor: (r) => coverageLabel(r.guardingCoverage) },
    { title: "Guarding Comments", accessor: (r) => r.guardingComments || "" },
    { title: "Alarm Equipment", accessor: (r) => alarmDevicesOf(r).join(", ") },
    { title: "Alarm Others", accessor: (r) => r.alarmOthers || "" },
    { title: "Install Quality", accessor: (r) => ratingLabel(r.installationQualityRating) },
    { title: "Alarm Coverage", accessor: (r) => ratingLabel(r.alarmCoverageRating) },
    { title: "Alarm Comments", accessor: (r) => r.alarmComments || "" },
  ];

  // The PDF drops the widest free-text columns - a printed visit list is a
  // "who went where" sheet; the full text is what Excel and the detail modal
  // are for.
  const pdfColumns = exportColumns.filter(
    (c) => !["Guarding Comments", "Alarm Comments", "Client Remarks", "Alarm Others"].includes(c.title),
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Client Visits</h4>
              <h6>
                Every recorded client visit - the salesperson&apos;s remarks, and the operations checklist where one was filled
              </h6>
            </div>
          </div>
        </div>

        {!canViewAll && (
          <div className="alert alert-info py-2 mb-3">
            You are seeing <strong>your own visits only</strong>. The <code>Sales.ViewAllClientVisits</code> permission
            widens this report to every visit in your branches.
          </div>
        )}

        {/* Accented stat tiles rather than five identical grey cards - the
            figure is the loudest thing, in its own colour, with an icon chip
            (feedback_flutter_ui_beautification_pattern, applied on the web
            side the same way the Sales/Stock dashboards do it). */}
        <div className="row g-2 mb-3">
          <StatTile icon={MapPin} label="Visits" value={stats.total} color={MAROON} />
          <StatTile
            icon={CheckSquare}
            label="With Checklist"
            value={stats.withChecklist}
            color="#1E8449"
            note={`${stats.total - stats.withChecklist} remarks only`}
          />
          <StatTile icon={Home} label="Customers Visited" value={stats.customers} color="#2E86AB" />
          <StatTile icon={Users} label="Staff Visiting" value={stats.visitors} color="#8E44AD" />
          <StatTile
            icon={AlertTriangle}
            label="Flagged Poor"
            value={stats.poor}
            color="#C0392B"
            note="any rating marked Poor"
            dim={stats.poor === 0}
          />
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2">
              <DateRangePicker
                preset={preset}
                setPreset={setPreset}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
              />
              <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Customer, visit no., contact, remarks"
                      className="form-control form-control-sm formsearch"
                      style={{ minWidth: 240, height: 34 }}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <select
                    className="form-control form-control-sm"
                    style={{ width: 170, height: 34 }}
                    value={checklistFilter}
                    onChange={(e) => setChecklistFilter(e.target.value)}
                  >
                    {CHECKLIST_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <select
                    className="form-control form-control-sm"
                    style={{ width: 190, height: 34 }}
                    value={visitorFilter}
                    onChange={(e) => setVisitorFilter(e.target.value)}
                    title="Filters the visits loaded for the selected date range"
                  >
                    <option value="">All staff</option>
                    {visitorNames.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <div style={{ width: 240 }} title="Every recorded visit to this customer">
                    <CustomerPicker branchId={selectedBranchId}
                      value={selectedCustomerOption}
                      onChange={setSelectedCustomerOption}
                      menuPortalTarget={document.body}
                      isClearable
                    />
                  </div>
                </div>
                <ColumnVisibilityMenu columns={ALL_COLUMNS} hidden={hiddenColumns} onToggle={toggleColumn} />
                {/* Added ALONGSIDE the existing Export PDF / Export Excel, not
                    instead of them (explicit instruction). This one prints the
                    list as a sheet; the exports are unchanged. */}
                <ClientVisitListPrintButton rows={rows} filterLines={printFilterLines} branchCode={branchCode} />
                <ListExportToolbar
                  title="Client Visits"
                  subtitleLines={printFilterLines}
                  branchCode={branchCode}
                  columns={exportColumns}
                  pdfColumns={pdfColumns}
                  rows={rows}
                  fileName="client-visits"
                  orientation="landscape"
                />
              </div>

              {isError && (
                <div className="alert alert-danger py-2 mb-0">
                  Could not load client visits{error?.message ? `: ${error.message}` : "."}
                </div>
              )}

              {/* Scrolls sideways inside the card. Without this wrapper the wide
                  table (14 columns by default) ran past the card's right edge
                  and the last columns were cut off (reported 2026-09-19). */}
              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  dataSource={rows}
                  rowKey="recordGuid"
                  loading={isLoading}
                  onRow={(record) => ({
                    onClick: () => setOpenVisit(record),
                    style: { cursor: "pointer" },
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {openVisit && (
        <ClientVisitDetailModal
          visit={openVisit}
          photoUrl={photoUrls[openVisit.visitedByEmployeeRecordGuid]}
          onClose={() => setOpenVisit(null)}
        />
      )}
    </div>
  );
};

export default ClientVisitsReportPage;
